import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/maps";
import { getPrice, getDeoMonthly } from "../lib/pricing";
import { DAY_COLORS, DAYS, FREQ_SHAPES, getServiceDayForClient, hasDayOverride } from "../lib/serviceArea";
import { haversineMiles, estimateMinutes } from "../lib/route";
import { fetchClientAvgMap, fmtClean } from "../lib/visitTimes";
import { zctasByCodes } from "../lib/zcta";
import { localDateStr } from "../lib/date";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";
import { useComms } from "../lib/commsReady";
import { Conversations } from "./Conversations";

// ════════════════════════════════════════════════════════════════════
//  ClientMap — "Live Map": the ops cockpit you open first thing in the morning.
//
//  header → metric row (each card is a filter) → left rail (search-first client
//  list) + hero map + slide-in right detail panel (master-detail, per the design
//  system). Three modes on one .seg: Today (status markers + numbered route
//  polylines + live tech dots) · All Clients (day-colored markers, clustered) ·
//  Territory (ZCTA polygons tinted by day, density/revenue heat toggles).
//
//  Zero new Google spend: search is in-memory, polylines are lat/lng only, tech
//  positions come from tech_presence (Supabase REST, 30s poll, paused when the
//  tab is hidden). Styles are inline (apple-native tokens) — see the
//  mobile-responsive gotchas memory: media queries can't override inline JSX.
// ════════════════════════════════════════════════════════════════════

const UNASSIGNED_COLOR = "#06B6D4";
const STATUS_COLORS = {
  scheduled: "#9CA3AF", started: "#0071E3", partial: "#0071E3", completed: "#1A8A52",
  skipped: "#D7322B", missed: "#D7322B", cancelled: "#C7C7CC", empty: "#C7C7CC",
};
const STATUS_LABEL = { scheduled: "Scheduled", started: "Started", partial: "Started", completed: "Done", skipped: "Skipped", missed: "Missed", cancelled: "Cancelled", empty: "Empty" };
const TERMINAL = new Set(["completed", "skipped", "missed", "cancelled", "empty"]);
const TECH_PALETTE = ["#6C5CE7", "#2BA3A3", "#C4694E", "#5A6AD0", "#B0568E", "#C19A2E", "#3B8E5A", "#4C8DBD"];
const AV_COLORS = ["#6C5CE7", "#2BA3A3", "#C4694E", "#5A6AD0", "#3B8E5A", "#B0568E", "#C19A2E", "#4C8DBD"];
const AMBER = { bg: "#FEF6E7", fg: "#8A5A06", line: "#F6D88A" };
const POLL_MS = 30000;
const PRESENCE_FRESH_MS = 30 * 60 * 1000;   // matches the DB sweeper's staleness window
const CLUSTER_MAX_ZOOM = 10;                // cluster at zoom ≤10, individual pins above
const RAIL_W = 340;
const PANEL_W = 340;
const TOPBAR_H = 60;

const H = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
const rest = async (path) => {
  try { const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, { headers: H() }); const d = await r.json(); return Array.isArray(d) ? d : []; }
  catch { return []; }
};

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "•";
const shortName = (name = "") => { const p = name.trim().split(/\s+/); return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : (p[0] || ""); };
const avColor = (s = "") => AV_COLORS[Math.abs([...s].reduce((a, ch) => a + ch.charCodeAt(0), 0)) % AV_COLORS.length];
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const fmtPhone = (p) => { const d = (p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || ""); };
const fmtFreq = (f) => f === "twice-weekly" ? "2×/wk" : f === "biweekly" ? "Bi-weekly" : f === "weekly" ? "Weekly" : (f || "No plan");
const fmtDate = (s) => s ? new Date(s + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" }) : "—";
const relSecs = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`; };
const num = (x) => (Number.isFinite(Number(x)) ? Number(x) : null);
const coordsOf = (c) => (c && num(c.lat) != null && num(c.lng) != null ? { lat: Number(c.lat), lng: Number(c.lng) } : null);
const clientMonthly = (c, pricing) => {
  let m = getPrice(c.frequency, c.dogs, pricing);
  if (c.deo_enabled && c.deo_status === "active") m += getDeoMonthly(c.deo_frequency, pricing);
  return m;
};
const statusPill = (c) => {
  const st = c.status || "active";
  const inactive = st === "deactivated" || st === "canceled" || !c.active;
  if (inactive) return { text: st === "canceled" ? "Canceled" : "Inactive", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" };
  if (st === "paused") return { text: "Paused", bg: AMBER.bg, fg: AMBER.fg };
  if (st === "payment_pending") return { text: "Past due", bg: "rgba(215,50,43,.12)", fg: "var(--danger)" };
  return { text: "Active", bg: "rgba(26,138,82,.14)", fg: "var(--ok)" };
};
const isPastDue = (c) => c.active && c.status === "payment_pending";
const isUnassigned = (c, override) => c.active && !(override?.[c.id] ? override[c.id].id : c.technician_id);

// ── Fuzzy search (local, no API). Scores name > address/zip/phone; substring
//    beats subsequence; word-prefix beats mid-word. Returns null on no match. ──
function norm(s) { return (s || "").toString().toLowerCase(); }
function subseq(hay, q) {
  let i = 0; for (const ch of hay) { if (ch === q[i]) i++; if (i === q.length) return true; } return false;
}
function scoreField(val, q, weight) {
  const h = norm(val); if (!h) return 0;
  if (h.startsWith(q)) return 100 * weight;
  if (h.split(/\s+/).some(w => w.startsWith(q))) return 80 * weight;
  const i = h.indexOf(q); if (i >= 0) return (60 - Math.min(i, 20)) * weight;
  if (q.length >= 3 && subseq(h, q)) return 20 * weight;
  return 0;
}
function scoreClient(c, q) {
  const digits = q.replace(/\D/g, "");
  const s = Math.max(
    scoreField(c.name, q, 3),
    scoreField(c.address, q, 1.6),
    scoreField(c.zip, q, 1.4),
    digits.length >= 3 ? scoreField((c.phone || "").replace(/\D/g, ""), digits, 1.5) : 0,
    scoreField(c.email, q, 1),
  );
  return s > 0 ? s : null;
}

// ── Marker icons: one SVG generator = one marker language. Cached per spec. ──
const iconCache = new Map();
function starPoints(cx, cy, R) {
  const r = R * 0.47, pts = [];
  for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + (i * Math.PI) / 5, rad = i % 2 ? r : R; pts.push(`${(cx + rad * Math.cos(a)).toFixed(1)},${(cy + rad * Math.sin(a)).toFixed(1)}`); }
  return pts.join(" ");
}
function markerIcon(g, { color, shape = "circle", ring = null, deo = false, label = null, hi = false, dim = false }) {
  const key = [color, shape, ring, deo, label, hi, dim].join("|");
  if (iconCache.has(key)) return iconCache.get(key);
  const S = hi ? 46 : 34, c = S / 2, r = hi ? 11.5 : 9;
  const fill = dim ? "#C7C7CC" : color;
  const stroke = "white";
  let body;
  if (shape === "diamond") body = `<rect x='${c - r}' y='${c - r}' width='${2 * r}' height='${2 * r}' rx='2' fill='${fill}' stroke='${stroke}' stroke-width='2.5' transform='rotate(45 ${c} ${c})'/>`;
  else if (shape === "star") body = `<polygon points='${starPoints(c, c, r + 2.5)}' fill='${fill}' stroke='${stroke}' stroke-width='2'/>`;
  else body = `<circle cx='${c}' cy='${c}' r='${r}' fill='${fill}' stroke='${stroke}' stroke-width='2.5'/>`;
  const halo = hi ? `<circle cx='${c}' cy='${c}' r='${r + 9}' fill='${fill}' fill-opacity='.22'/>` : "";
  const ringEl = ring ? `<circle cx='${c}' cy='${c}' r='${r + 4}' fill='none' stroke='${ring}' stroke-width='2.5'/>` : "";
  const lab = label != null ? `<text x='${c}' y='${c + 3.6}' text-anchor='middle' font-family='-apple-system,Inter,Helvetica,sans-serif' font-size='${hi ? 11 : 10}' font-weight='700' fill='white'>${label}</text>` : "";
  const badge = deo ? `<circle cx='${c + r - 0.5}' cy='${c - r + 0.5}' r='6' fill='white' stroke='${fill}' stroke-width='1'/><text x='${c + r - 0.5}' y='${c - r + 3.6}' text-anchor='middle' font-size='8'>💨</text>` : "";
  const url = "data:image/svg+xml," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${S}' height='${S}'>${halo}${ringEl}${body}${lab}${badge}</svg>`);
  const icon = { url, scaledSize: new g.maps.Size(S, S), anchor: new g.maps.Point(c, c) };
  iconCache.set(key, icon);
  return icon;
}

// ── Tech dot: an HTML overlay (avatar bubble + pulsing ring + name chip). Built
//    lazily because it must extend google.maps.OverlayView at runtime. ──
let TechOverlayClass = null;
function getTechOverlayClass(g) {
  if (TechOverlayClass) return TechOverlayClass;
  class TechOverlay extends g.maps.OverlayView {
    constructor(opts) { super(); this.opts = opts; this.div = null; }
    onAdd() {
      const d = document.createElement("div");
      d.style.cssText = "position:absolute;cursor:pointer;transform:translate(-50%,-50%);z-index:60;user-select:none;-webkit-user-select:none";
      d.addEventListener("click", (e) => { e.stopPropagation(); this.opts.onClick && this.opts.onClick(); });
      d.addEventListener("mouseenter", () => this.opts.onHover && this.opts.onHover(true));
      d.addEventListener("mouseleave", () => this.opts.onHover && this.opts.onHover(false));
      this.div = d;
      this.getPanes().overlayMouseTarget.appendChild(d);
      this.render();
    }
    draw() {
      const p = this.getProjection(); if (!p || !this.div) return;
      const pt = p.fromLatLngToDivPixel(new g.maps.LatLng(this.opts.lat, this.opts.lng));
      if (pt) { this.div.style.left = `${pt.x}px`; this.div.style.top = `${pt.y}px`; }
    }
    onRemove() { if (this.div) { this.div.remove(); this.div = null; } }
    update(opts) { this.opts = { ...this.opts, ...opts }; this.render(); this.draw(); }
    render() {
      if (!this.div) return;
      const { color, initial, onSite, chip, selected, stale } = this.opts;
      this.div.style.opacity = stale ? ".55" : "1";
      this.div.innerHTML =
        `<div style="position:relative;width:38px;height:38px;display:flex;align-items:center;justify-content:center">` +
        (onSite ? `<span class="lm-pulse" style="position:absolute;inset:-7px;border-radius:50%;border:3px solid ${color}"></span>` : "") +
        `<span style="width:34px;height:34px;border-radius:50%;background:${color};color:#fff;font-weight:700;font-size:14px;display:flex;align-items:center;justify-content:center;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.28)${selected ? `,0 0 0 3px ${color}` : ""}">${initial}</span></div>` +
        (chip ? `<div style="position:absolute;top:100%;left:50%;transform:translate(-50%,3px);white-space:nowrap;background:rgba(29,29,31,.9);color:#fff;font-size:11px;font-weight:600;padding:3px 9px;border-radius:999px;box-shadow:0 2px 6px rgba(0,0,0,.22);letter-spacing:-.01em">${chip}</div>` : "");
    }
  }
  TechOverlayClass = TechOverlay;
  return TechOverlay;
}

// Area-weighted centroid (shoelace) of the largest ring — where a ZIP label sits.
function centroidOf(paths) {
  let best = null, bestArea = 0;
  for (const ring of paths || []) {
    if (!ring || ring.length < 3) continue;
    let a = 0, cx = 0, cy = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const f = ring[j].lng * ring[i].lat - ring[i].lng * ring[j].lat;
      a += f; cx += (ring[j].lng + ring[i].lng) * f; cy += (ring[j].lat + ring[i].lat) * f;
    }
    if (Math.abs(a) > Math.abs(bestArea)) { bestArea = a; best = { lat: cy / (3 * a), lng: cx / (3 * a) }; }
  }
  return best && Number.isFinite(best.lat) && Number.isFinite(best.lng) ? best : null;
}
const zctaCache = new Map(); // zip -> paths (module-level: boundaries never change within a session)

const CSS_ID = "scoop-livemap-css";
const CSS = `
@keyframes lm-pulse{0%{transform:scale(.7);opacity:.9}100%{transform:scale(1.9);opacity:0}}
.lm-pulse{animation:lm-pulse 1.6s ease-out infinite}
.lm-row{display:flex;align-items:center;gap:10px;padding:9px 10px;border-radius:10px;cursor:pointer;transition:background .12s}
.lm-row:hover{background:var(--bg)}
.lm-row.hov{background:var(--bg)}
.lm-row.sel{background:var(--accent-weak);box-shadow:inset 0 0 0 1px rgba(0,113,227,.25)}
.lm-scroll{scrollbar-width:thin}
.lm-scroll::-webkit-scrollbar{width:6px}.lm-scroll::-webkit-scrollbar-thumb{background:rgba(0,0,0,.12);border-radius:3px}
.lm-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--r);padding:12px 14px;cursor:pointer;transition:transform .16s,box-shadow .16s,border-color .16s;box-shadow:var(--shadow-sm);text-align:left;font:inherit;min-width:0}
.lm-card:hover{transform:translateY(-1px);box-shadow:var(--shadow)}
.lm-card.on{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-weak)}
.lm-chip{border:1px solid var(--border);background:rgba(255,255,255,.92);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-radius:999px;padding:6px 11px;font:inherit;font-size:12px;font-weight:600;color:var(--text2);cursor:pointer;box-shadow:var(--shadow-sm);white-space:nowrap}
.lm-chip.on{background:var(--accent);color:#fff;border-color:var(--accent)}
.lm-ib{width:30px;height:30px;border-radius:8px;border:1px solid var(--border);background:var(--surface);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;font-size:13px;color:var(--text2);font:inherit}
.lm-ib:hover{background:var(--bg)}
.lm-field{display:flex;justify-content:space-between;gap:12px;font-size:12.5px;padding:8px 0;border-bottom:1px solid var(--border);align-items:center}
.lm-field>span:first-child{color:var(--text2);flex-shrink:0}
.lm-field>span:last-child{color:var(--text);text-align:right;overflow-wrap:anywhere;font-weight:500}
.lm-kbd{font-size:10px;font-weight:600;color:var(--text3);border:1px solid var(--border);border-radius:5px;padding:1px 5px;background:var(--surface);line-height:1.4}
`;

export function ClientMap({
  clients = [], hovered, setHovered, mapFilter = "all", setMapFilter, serviceArea = {},
  setPage, technicians = [], pricing, showToast, onAssignTech, onViewProfile,
}) {
  const isMobile = useIsMobile();
  const { requireTexting } = useComms();
  const g = typeof window !== "undefined" ? window.google : null;

  // ── UI state ───────────────────────────────────────────────────────────────
  const [mode, setMode] = useState("all");              // all | today | territory (owner: open on All Clients)
  const [cardFilter, setCardFilter] = useState(null);   // done | onsite | unassigned | pastdue
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [sel, setSel] = useState(null);                 // { type:'client'|'tech', id }
  const [railOpen, setRailOpen] = useState(true);
  const [sheet, setSheet] = useState("peek");           // mobile: peek | open
  const [panelView, setPanelView] = useState("detail"); // detail | messages
  const [showTrail, setShowTrail] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showRevenue, setShowRevenue] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(!isMobile);
  const [mapReady, setMapReady] = useState(false);
  const [now, setNow] = useState(Date.now());

  // ── Live data ──────────────────────────────────────────────────────────────
  const [presence, setPresence] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [lastPoll, setLastPoll] = useState(null);
  const [cleanMap, setCleanMap] = useState({});
  const [recent, setRecent] = useState([]);
  const [nextVisit, setNextVisit] = useState(null);
  const [trail, setTrail] = useState([]);
  const [zctaLoading, setZctaLoading] = useState(false);
  const [zipHover, setZipHover] = useState(null);
  const [localTech, setLocalTech] = useState({});       // optimistic client→tech override until App refreshes
  const [pendingTech, setPendingTech] = useState(null); // { id, name, count } → confirm chip
  const [reassigning, setReassigning] = useState(false);

  const todayStr = localDateStr();

  // ── Refs (Google objects live outside React state) ─────────────────────────
  const mapRef = useRef(null), mapObj = useRef(null), wrapRef = useRef(null);
  const markersRef = useRef({});        // clientId -> { marker, spec }
  const clustererRef = useRef(null);
  const techOverlaysRef = useRef({});   // techId -> overlay
  const routeLinesRef = useRef([]);
  const trailLineRef = useRef(null);
  const zctaRef = useRef({});           // zip -> { poly, label }
  const heatRef = useRef(null), revRef = useRef(null);
  const didFitRef = useRef(false);
  const searchRef = useRef(null), listRef = useRef(null);
  const pollTimerRef = useRef(null);
  const selRef = useRef(sel); selRef.current = sel;
  const isMobileRef = useRef(isMobile); isMobileRef.current = isMobile;
  const lastTapZipRef = useRef(null);
  const hoveredRef = useRef(hovered); hoveredRef.current = hovered;

  useEffect(() => {
    if (!document.getElementById(CSS_ID)) { const st = document.createElement("style"); st.id = CSS_ID; st.textContent = CSS; document.head.appendChild(st); }
  }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(t); }, []);
  useEffect(() => { setLegendOpen(!isMobile); }, [isMobile]);

  // ── Derived: clients, techs, today's stops ─────────────────────────────────
  const clientById = useMemo(() => { const m = {}; clients.forEach(c => { m[c.id] = c; }); return m; }, [clients]);
  const clientByName = useMemo(() => { const m = {}; clients.forEach(c => { m[norm(c.name)] = c; }); return m; }, [clients]);
  const techColor = useCallback((id) => { const i = technicians.findIndex(t => t.id === id); return TECH_PALETTE[(i < 0 ? 0 : i) % TECH_PALETTE.length]; }, [technicians]);
  const techById = useMemo(() => { const m = {}; technicians.forEach(t => { m[t.id] = t; }); return m; }, [technicians]);
  const techOf = useCallback((c) => localTech[c.id] ? localTech[c.id] : { id: c.technician_id, name: c.technician_name }, [localTech]);

  const freshPresence = useMemo(() => presence.filter(p => p.updated_at && (now - new Date(p.updated_at).getTime()) < PRESENCE_FRESH_MS && num(p.lat) != null && num(p.lng) != null), [presence, now]);
  const presenceByTech = useMemo(() => { const m = {}; freshPresence.forEach(p => { m[p.technician_id] = p; }); return m; }, [freshPresence]);
  const onSiteClientOf = useCallback((p) => {
    if (!p || !p.on_site || !p.on_site_client) return null;
    return clientById[p.on_site_client] || clientByName[norm(p.on_site_client)] || null;
  }, [clientById, clientByName]);

  const headerByClient = useMemo(() => { const m = {}; headers.forEach(h => { if (!m[h.client_id] || (h.source === "auto" && m[h.client_id].source !== "auto")) m[h.client_id] = h; }); return m; }, [headers]);
  const stopsByTech = useMemo(() => {
    const m = {};
    headers.forEach(h => { const k = h.technician_id || "__none__"; (m[k] ||= []).push(h); });
    Object.values(m).forEach(arr => arr.sort((a, b) => (a.route_seq ?? 9999) - (b.route_seq ?? 9999) || (a.client_name || "").localeCompare(b.client_name || "")));
    return m;
  }, [headers]);
  const doneCount = useMemo(() => headers.filter(h => h.status === "completed").length, [headers]);
  const totalStops = headers.length;
  const onSiteCount = useMemo(() => freshPresence.filter(p => p.on_site).length, [freshPresence]);
  const unassignedCount = useMemo(() => clients.filter(c => isUnassigned(c, localTech)).length, [clients, localTech]);
  const pastDueCount = useMemo(() => clients.filter(isPastDue).length, [clients]);

  // Techs that belong in the rail: live (fresh presence) ∪ anyone with stops today.
  const railTechs = useMemo(() => {
    const ids = new Set(Object.keys(presenceByTech));
    if (mode === "today") Object.keys(stopsByTech).forEach(k => { if (k !== "__none__") ids.add(k); });
    return [...ids].map(id => {
      const t = techById[id]; const p = presenceByTech[id]; const stops = stopsByTech[id] || [];
      const name = t?.name || p?.technician_name || headers.find(h => h.technician_id === id)?.technician_name || "Tech";
      const done = stops.filter(s => s.status === "completed").length;
      const next = stops.find(s => !TERMINAL.has(s.status));
      const atClient = onSiteClientOf(p);
      return { id, name, color: techColor(id), presence: p, stops, done, remaining: stops.filter(s => !TERMINAL.has(s.status)).length, next, atClient, home: t && num(t.home_lat) != null && num(t.home_lng) != null ? { lat: +t.home_lat, lng: +t.home_lng } : null };
    }).sort((a, b) => (b.presence ? 1 : 0) - (a.presence ? 1 : 0) || a.name.localeCompare(b.name));
  }, [presenceByTech, stopsByTech, techById, mode, headers, onSiteClientOf, techColor]);
  const railTechById = useMemo(() => { const m = {}; railTechs.forEach(t => { m[t.id] = t; }); return m; }, [railTechs]);

  // ── Visible clients: mode → list filter → metric-card filter → search ──────
  const q = norm(query.trim());
  const visibleClients = useMemo(() => {
    let list = clients.filter(c => coordsOf(c) || q); // unplaced clients stay searchable
    if (mode === "today") list = list.filter(c => headerByClient[c.id]);
    if (mapFilter === "active") list = list.filter(c => c.active);
    else if (mapFilter === "inactive") list = list.filter(c => !c.active);
    else if (mapFilter && mapFilter !== "all" && !DAYS.includes(mapFilter)) list = list.filter(c => c.frequency === mapFilter);
    else if (DAYS.includes(mapFilter)) list = list.filter(c => getServiceDayForClient(c, serviceArea) === mapFilter);
    if (cardFilter === "done") list = list.filter(c => headerByClient[c.id]?.status === "completed");
    else if (cardFilter === "unassigned") list = list.filter(c => isUnassigned(c, localTech));
    else if (cardFilter === "pastdue") list = list.filter(isPastDue);
    else if (cardFilter === "onsite") { const ids = new Set(freshPresence.map(p => onSiteClientOf(p)?.id).filter(Boolean)); list = list.filter(c => ids.has(c.id)); }
    if (q) {
      list = list.map(c => ({ c, s: scoreClient(c, q) })).filter(x => x.s != null).sort((a, b) => b.s - a.s || a.c.name.localeCompare(b.c.name)).map(x => x.c);
    } else if (mode === "today") {
      list = list.slice().sort((a, b) => { const ha = headerByClient[a.id], hb = headerByClient[b.id]; return (ha.technician_name || "zz").localeCompare(hb.technician_name || "zz") || (ha.route_seq ?? 9999) - (hb.route_seq ?? 9999) || a.name.localeCompare(b.name); });
    } else list = list.slice().sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [clients, mode, mapFilter, cardFilter, q, headerByClient, localTech, freshPresence, onSiteClientOf, serviceArea]);
  const visibleTechs = useMemo(() => {
    let t = railTechs;
    if (cardFilter === "onsite") t = t.filter(x => x.presence?.on_site);
    else if (cardFilter && cardFilter !== "done") t = [];
    if (q) t = t.filter(x => scoreField(x.name, q, 1) > 0);
    return t;
  }, [railTechs, cardFilter, q]);
  // One flat keyboard-navigable list. Browsing: techs first (the live layer), then
  // clients. Searching: CLIENTS first — "kev" + Enter must land on the house, not
  // on tech Kevin's dot.
  const railItems = useMemo(() => {
    const t = visibleTechs.map(x => ({ type: "tech", id: x.id })), c = visibleClients.map(x => ({ type: "client", id: x.id }));
    return q ? [...c, ...t] : [...t, ...c];
  }, [visibleTechs, visibleClients, q]);
  useEffect(() => { setActiveIdx(0); }, [q, mode, cardFilter, mapFilter]);

  const selClient = sel?.type === "client" ? clientById[sel.id] : null;
  const selTech = sel?.type === "tech" ? railTechById[sel.id] : null;

  // ── Polling: tech_presence + today's job_headers, 30s, paused when hidden ──
  const pollLive = useCallback(async () => {
    const since = new Date(Date.now() - PRESENCE_FRESH_MS).toISOString();
    const [p, h] = await Promise.all([
      rest(`tech_presence?updated_at=gte.${encodeURIComponent(since)}&select=technician_id,technician_name,lat,lng,on_site,on_site_client,status,updated_at`),
      rest(`job_headers?scheduled_date=eq.${localDateStr()}&status=not.in.(cancelled,empty)&select=id,client_id,client_name,technician_id,technician_name,status,route_seq,route_leg_minutes,started_at,completed_at,duration_mins,source&order=route_seq.asc.nullslast`),
    ]);
    setPresence(p); setHeaders(h); setLastPoll(Date.now());
  }, []);
  useEffect(() => {
    let dead = false;
    const start = () => { if (dead) return; stop(); pollLive(); pollTimerRef.current = setInterval(pollLive, POLL_MS); };
    const stop = () => { if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; } };
    const onVis = () => { if (document.hidden) stop(); else start(); };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => { dead = true; stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [pollLive]);
  useEffect(() => { fetchClientAvgMap().then(m => setCleanMap(m || {})); }, []);

  // ── Selected client: recent visits + next visit ────────────────────────────
  useEffect(() => {
    if (!selClient) { setRecent([]); setNextVisit(null); return undefined; }
    let cancelled = false;
    const id = selClient.id;
    Promise.all([
      rest(`job_headers?client_id=eq.${id}&scheduled_date=lte.${todayStr}&status=not.in.(cancelled,empty)&order=scheduled_date.desc&limit=5&select=id,scheduled_date,status,technician_name,completed_at,duration_mins`),
      rest(`job_headers?client_id=eq.${id}&scheduled_date=gte.${todayStr}&status=in.(scheduled,started)&order=scheduled_date.asc&limit=1&select=scheduled_date,technician_name`),
    ]).then(([r, n]) => { if (cancelled) return; setRecent(r); setNextVisit(n[0] || null); });
    return () => { cancelled = true; };
  }, [selClient?.id, todayStr]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPendingTech(null); setPanelView("detail"); }, [sel?.id, sel?.type]);

  // ── Selected tech: breadcrumb trail (today's pings), only when toggled on ──
  useEffect(() => {
    if (!selTech || !showTrail) { setTrail([]); return undefined; }
    let cancelled = false;
    const start = new Date(); start.setHours(0, 0, 0, 0);
    rest(`tech_presence_pings?technician_id=eq.${selTech.id}&recorded_at=gte.${encodeURIComponent(start.toISOString())}&select=lat,lng,recorded_at&order=recorded_at.asc&limit=3000`)
      .then(rows => { if (!cancelled) setTrail(rows.filter(r => num(r.lat) != null && num(r.lng) != null).map(r => ({ lat: +r.lat, lng: +r.lng }))); });
    return () => { cancelled = true; };
  }, [selTech?.id, showTrail]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Map init ───────────────────────────────────────────────────────────────
  useEffect(() => { loadGoogleMaps().then(() => setMapReady(true)).catch(e => console.error("Google Maps failed:", e)); }, []);
  useEffect(() => {
    if (!mapReady || !mapRef.current || mapObj.current) return;
    const G = window.google;
    mapObj.current = new G.maps.Map(mapRef.current, {
      zoom: 11, center: { lat: 39.9526, lng: -75.1652 },
      mapTypeControl: false, streetViewControl: false, fullscreenControl: false, zoomControl: !isMobile,
      gestureHandling: "greedy", clickableIcons: false,
      styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }, { featureType: "transit", stylers: [{ visibility: "off" }] }],
    });
    mapObj.current.addListener("click", () => { setSel(null); setFilterOpen(false); });
    clustererRef.current = new MarkerClusterer({
      map: mapObj.current, markers: [],
      algorithm: new SuperClusterAlgorithm({ maxZoom: CLUSTER_MAX_ZOOM, radius: 90 }),
      renderer: { render: ({ count, position }) => new G.maps.Marker({
        position, zIndex: 1000 + count,
        icon: { url: "data:image/svg+xml," + encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='46' height='46'><circle cx='23' cy='23' r='22' fill='#0071E3' fill-opacity='.18'/><circle cx='23' cy='23' r='16' fill='#0071E3' stroke='white' stroke-width='2.5'/></svg>`), scaledSize: new G.maps.Size(46, 46), anchor: new G.maps.Point(23, 23) },
        label: { text: String(count), color: "#fff", fontSize: "12px", fontWeight: "700" },
      }) },
    });
  }, [mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the map sized to its container (rail collapse / panel / resize).
  useEffect(() => { if (mapObj.current && g) { const c = mapObj.current.getCenter(); g.maps.event.trigger(mapObj.current, "resize"); if (c) mapObj.current.setCenter(c); } }, [railOpen, isMobile, g]);

  const flyTo = useCallback((pos, zoom = 16) => {
    const m = mapObj.current; if (!m || !pos) return;
    m.panTo(pos);
    if ((m.getZoom() || 0) < zoom) setTimeout(() => m.setZoom(zoom), 260);
  }, []);
  const bounce = useCallback((id) => {
    const e = markersRef.current[id]; const G = window.google; if (!e || !G) return;
    e.marker.setAnimation(G.maps.Animation.BOUNCE);
    setTimeout(() => e.marker.setAnimation(null), 720);
  }, []);
  const selectClient = useCallback((c, { fly = true } = {}) => {
    if (!c) return;
    setSel({ type: "client", id: c.id });
    setHovered && setHovered(c.id);
    if (fly) { const pos = coordsOf(c); if (pos) { flyTo(pos); setTimeout(() => bounce(c.id), 300); } else showToast && showToast(`${c.name} has no map location yet`, "error"); }
    if (isMobile) setSheet("peek");
  }, [flyTo, bounce, isMobile, setHovered, showToast]);
  const selectTech = useCallback((t, { fly = true } = {}) => {
    if (!t) return;
    setSel({ type: "tech", id: t.id });
    const p = t.presence; if (fly && p) flyTo({ lat: +p.lat, lng: +p.lng }, 14);
    if (isMobile) setSheet("peek");
  }, [flyTo, isMobile]);
  const closePanel = useCallback(() => { setSel(null); setPanelView("detail"); }, []);
  useBackClose(!!sel && isMobile, closePanel);

  // ── Client marker spec (the marker language) ───────────────────────────────
  const specFor = useCallback((c) => {
    const h = headerByClient[c.id];
    if (mode === "today" && h) return { color: STATUS_COLORS[h.status] || STATUS_COLORS.scheduled, shape: "circle", label: h.route_seq ?? null, ring: null, deo: false, dim: false };
    const un = isUnassigned(c, localTech);
    const color = un ? UNASSIGNED_COLOR : (DAY_COLORS[getServiceDayForClient(c, serviceArea)] || NAVY);
    return { color, shape: FREQ_SHAPES[c.frequency] || "circle", ring: c.active && c.status === "payment_pending" ? "#D7322B" : c.active && c.status === "paused" ? "#E9A93C" : null, deo: !!c.deo_enabled && c.active, label: null, dim: !c.active };
  }, [mode, headerByClient, localTech, serviceArea]);

  // ── Client markers: diff-based rebuild; clustered outside Today mode ───────
  const visibleKey = visibleClients.map(c => c.id).join(",");
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G) return;
    const keep = new Set();
    const list = [];
    visibleClients.forEach(c => {
      const pos = coordsOf(c); if (!pos) return;
      keep.add(c.id);
      const spec = specFor(c);
      const hi = hoveredRef.current === c.id || (selRef.current?.type === "client" && selRef.current.id === c.id);
      const icon = markerIcon(G, { ...spec, hi });
      let e = markersRef.current[c.id];
      if (!e) {
        const marker = new G.maps.Marker({ position: pos, icon, title: c.name, zIndex: hi ? 500 : 10, optimized: true });
        marker.addListener("click", () => { const cc = clientById[marker.__cid]; if (cc) selectClient(cc, { fly: false }); });
        marker.addListener("mouseover", () => setHovered && setHovered(marker.__cid));
        marker.addListener("mouseout", () => setHovered && setHovered(null));
        e = markersRef.current[c.id] = { marker, spec };
      } else { e.marker.setPosition(pos); e.marker.setIcon(icon); e.spec = spec; }
      e.marker.__cid = c.id;
      list.push(e.marker);
    });
    Object.keys(markersRef.current).forEach(id => { if (!keep.has(id)) { markersRef.current[id].marker.setMap(null); delete markersRef.current[id]; } });
    const cl = clustererRef.current;
    if (cl) cl.clearMarkers(true);
    if (mode === "today") list.forEach(mk => mk.setMap(m));
    else if (cl) cl.addMarkers(list);
    if (!didFitRef.current && list.length) {
      const b = new G.maps.LatLngBounds(); list.forEach(mk => b.extend(mk.getPosition()));
      m.fitBounds(b, 40);
      G.maps.event.addListenerOnce(m, "idle", () => { if ((m.getZoom() || 0) > 14) m.setZoom(14); });
      didFitRef.current = true;
    }
  }, [mapReady, visibleKey, specFor, mode, clientById, selectClient, setHovered]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hover / selection highlight — touch only the two markers that changed.
  const prevHiRef = useRef([]);
  useEffect(() => {
    const G = window.google; if (!mapReady || !G) return;
    const nowHi = [hovered, sel?.type === "client" ? sel.id : null].filter(Boolean);
    [...new Set([...prevHiRef.current, ...nowHi])].forEach(id => {
      const e = markersRef.current[id]; if (!e) return;
      const hi = nowHi.includes(id);
      e.marker.setIcon(markerIcon(G, { ...e.spec, hi }));
      e.marker.setZIndex(hi ? 500 : 10);
    });
    prevHiRef.current = nowHi;
  }, [hovered, sel, mapReady, visibleKey]);

  // ── Tech dots ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G) return;
    const TechOverlay = getTechOverlayClass(G);
    const keep = new Set();
    railTechs.forEach(t => {
      const p = t.presence; if (!p) return;
      keep.add(t.id);
      const atClient = t.atClient;
      const h = atClient ? headerByClient[atClient.id] : null;
      const mins = h?.started_at ? Math.max(0, Math.round((now - new Date(h.started_at).getTime()) / 60000)) : null;
      const chip = p.on_site ? `${t.name.split(" ")[0]} · at ${atClient ? shortName(atClient.name) : (p.on_site_client || "a stop")}${mins != null ? ` · ${mins} min` : ""}` : `${t.name.split(" ")[0]}${p.status === "driving" ? " · driving" : ""}`;
      const opts = { lat: +p.lat, lng: +p.lng, color: t.color, initial: initials(t.name).slice(0, 1), onSite: !!p.on_site, chip, selected: sel?.type === "tech" && sel.id === t.id, stale: (now - new Date(p.updated_at).getTime()) > 5 * 60000,
        onClick: () => selectTech(railTechById[t.id] || t, { fly: false }) };
      let ov = techOverlaysRef.current[t.id];
      if (!ov) { ov = new TechOverlay(opts); ov.setMap(m); techOverlaysRef.current[t.id] = ov; } else ov.update(opts);
    });
    Object.keys(techOverlaysRef.current).forEach(id => { if (!keep.has(id)) { techOverlaysRef.current[id].setMap(null); delete techOverlaysRef.current[id]; } });
  }, [mapReady, railTechs, railTechById, headerByClient, sel, now, selectTech]);

  // ── Today mode: per-tech route polylines (lat/lng only — no Directions) ────
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G) return;
    routeLinesRef.current.forEach(l => l.setMap(null)); routeLinesRef.current = [];
    if (mode !== "today") return;
    railTechs.forEach(t => {
      if (sel?.type === "tech" && sel.id !== t.id) return; // focus: only the selected tech's route
      const pts = [];
      if (t.home) pts.push(t.home);
      t.stops.forEach(s => { const c = clientById[s.client_id]; const pos = coordsOf(c); if (pos && visibleClients.some(v => v.id === s.client_id)) pts.push(pos); });
      if (pts.length < 2) return;
      const line = new G.maps.Polyline({ map: m, path: pts, strokeColor: t.color, strokeOpacity: .7, strokeWeight: 3, zIndex: 5, clickable: false,
        icons: [{ icon: { path: G.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.2, strokeColor: t.color, fillColor: t.color, fillOpacity: 1 }, offset: "50%", repeat: "140px" }] });
      routeLinesRef.current.push(line);
    });
  }, [mapReady, mode, railTechs, clientById, sel, visibleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Breadcrumb trail ───────────────────────────────────────────────────────
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G) return;
    if (trailLineRef.current) { trailLineRef.current.setMap(null); trailLineRef.current = null; }
    if (!showTrail || !selTech || trail.length < 2) return;
    trailLineRef.current = new G.maps.Polyline({ map: m, path: trail, strokeColor: selTech.color, strokeOpacity: 0, zIndex: 4, clickable: false,
      icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: .55, strokeColor: selTech.color, scale: 2 }, offset: "0", repeat: "8px" }] });
  }, [mapReady, showTrail, trail, selTech]);

  // ── Territory mode: ZCTA polygons tinted by day, opacity by client density ──
  const zipStats = useMemo(() => {
    const s = {};
    clients.forEach(c => { if (!c.zip) return; const e = (s[c.zip] ||= { count: 0, active: 0, revenue: 0 }); e.count++; if (c.active) { e.active++; e.revenue += clientMonthly(c, pricing); } });
    return s;
  }, [clients, pricing]);
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G) return;
    const setVisible = (v) => Object.values(zctaRef.current).forEach(e => { e.poly.setMap(v ? m : null); e.label && e.label.setMap(v ? m : null); });
    if (mode !== "territory") { setVisible(false); setZipHover(null); return undefined; }
    const dayOf = {};
    Object.entries(serviceArea || {}).forEach(([d, zips]) => (zips || []).forEach(z => { dayOf[z] = d; }));
    Object.keys(zipStats).forEach(z => { if (!dayOf[z]) dayOf[z] = null; }); // client zips outside the saved area still get a (gray) shape
    const zips = Object.keys(dayOf);
    const maxActive = Math.max(1, ...Object.values(zipStats).map(s => s.active));
    let cancelled = false;
    const draw = (zip, paths) => {
      if (!paths) return;
      const day = dayOf[zip]; const col = day ? (DAY_COLORS[day] || "#666") : "#8E8E93";
      const st = zipStats[zip] || { count: 0, active: 0, revenue: 0 };
      const fill = .10 + Math.min(st.active / maxActive, 1) * .38;
      let e = zctaRef.current[zip];
      if (!e) {
        const poly = new G.maps.Polygon({ map: m, paths, zIndex: 1, strokeColor: col, strokeOpacity: .8, strokeWeight: 1.2, fillColor: col, fillOpacity: fill, clickable: true });
        poly.addListener("mouseover", () => poly.setOptions({ fillOpacity: Math.min(fill + .18, .75), strokeWeight: 2.2 }));
        poly.addListener("mousemove", (ev) => { const wrap = wrapRef.current; if (!wrap || !ev.domEvent) return; const r = wrap.getBoundingClientRect(); setZipHover({ zip, x: ev.domEvent.clientX - r.left, y: ev.domEvent.clientY - r.top }); });
        poly.addListener("mouseout", () => { poly.setOptions({ fillOpacity: poly.__fill, strokeWeight: 1.2 }); setZipHover(null); });
        poly.addListener("click", (ev) => {
          if (isMobileRef.current && lastTapZipRef.current !== zip) {
            lastTapZipRef.current = zip;
            const wrap = wrapRef.current, r = wrap && wrap.getBoundingClientRect();
            if (r && ev.domEvent) setZipHover({ zip, x: Math.min(ev.domEvent.clientX - r.left, r.width - 190), y: ev.domEvent.clientY - r.top });
            return; // touch has no hover: first tap = tooltip, second tap = open the list
          }
          lastTapZipRef.current = null;
          setMapFilter && setMapFilter("all"); setQuery(zip); setMode("all");
        });
        const c = centroidOf(paths);
        const label = c ? new G.maps.Marker({ map: m, position: c, clickable: false, zIndex: 2, icon: { path: G.maps.SymbolPath.CIRCLE, scale: 0 },
          label: { text: `${zip}${st.active ? ` · ${st.active}` : ""}`, fontSize: "11px", fontWeight: "700", color: "#1d1d1f", className: "scoop-zcta-label" } }) : null;
        e = zctaRef.current[zip] = { poly, label };
      } else { e.poly.setOptions({ strokeColor: col, fillColor: col, fillOpacity: fill }); e.poly.setMap(m); e.label && e.label.setMap(m); }
      e.poly.__fill = fill;
    };
    if (!document.getElementById("scoop-zcta-label-css")) { const st = document.createElement("style"); st.id = "scoop-zcta-label-css"; st.textContent = ".scoop-zcta-label{text-shadow:0 0 3px #fff,0 0 5px #fff,0 1px 2px rgba(255,255,255,.95)}"; document.head.appendChild(st); }
    zips.filter(z => zctaCache.has(z)).forEach(z => draw(z, zctaCache.get(z)));
    const toFetch = zips.filter(z => !zctaCache.has(z));
    if (toFetch.length) {
      setZctaLoading(true);
      zctasByCodes(toFetch).then(zs => { zs.forEach(z => zctaCache.set(z.zip, z.paths)); if (!cancelled) toFetch.forEach(z => zctaCache.has(z) && draw(z, zctaCache.get(z))); })
        .catch(() => showToast && showToast("Couldn't load ZIP boundaries", "error"))
        .finally(() => { if (!cancelled) setZctaLoading(false); });
    }
    return () => { cancelled = true; };
  }, [mapReady, mode, serviceArea, zipStats]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heat overlays (density / revenue) — Territory-mode toggles, kept from the old page.
  useEffect(() => {
    const m = mapObj.current, G = window.google; if (!mapReady || !m || !G?.maps?.visualization) return;
    const on = mode === "territory";
    const pts = (weightOf) => clients.filter(c => coordsOf(c)).map(c => ({ location: new G.maps.LatLng(+c.lat, +c.lng), weight: weightOf(c) }));
    if (on && showHeatmap) {
      const data = pts(c => zipStats[c.zip]?.count || 1);
      if (!heatRef.current) heatRef.current = new G.maps.visualization.HeatmapLayer({ data, map: m, radius: 40, opacity: .7, gradient: ["rgba(0,255,0,0)", "rgba(0,255,0,1)", "rgba(255,255,0,1)", "rgba(255,0,0,1)"] });
      else { heatRef.current.setData(data); heatRef.current.setMap(m); }
    } else heatRef.current && heatRef.current.setMap(null);
    if (on && showRevenue) {
      const data = pts(c => zipStats[c.zip]?.revenue || 1);
      if (!revRef.current) revRef.current = new G.maps.visualization.HeatmapLayer({ data, map: m, radius: 50, opacity: .65, gradient: ["rgba(147,197,253,0)", "rgba(59,130,246,1)", "rgba(124,58,237,1)"] });
      else { revRef.current.setData(data); revRef.current.setMap(m); }
    } else revRef.current && revRef.current.setMap(null);
  }, [mapReady, mode, showHeatmap, showRevenue, clients, zipStats]);

  // Cleanup on unmount
  useEffect(() => () => {
    Object.values(markersRef.current).forEach(e => e.marker.setMap(null));
    Object.values(techOverlaysRef.current).forEach(o => o.setMap(null));
    routeLinesRef.current.forEach(l => l.setMap(null));
    trailLineRef.current && trailLineRef.current.setMap(null);
    Object.values(zctaRef.current).forEach(e => { e.poly.setMap(null); e.label && e.label.setMap(null); });
    heatRef.current && heatRef.current.setMap(null); revRef.current && revRef.current.setMap(null);
    clustererRef.current && clustererRef.current.clearMarkers(true);
  }, []);

  // ── Keyboard: ⌘K / "/" focus search from anywhere; arrows + Enter in the box ─
  const focusSearch = useCallback(() => {
    setRailOpen(true); if (isMobile) setSheet("open");
    requestAnimationFrame(() => { const el = searchRef.current; if (el) { el.focus(); el.select(); } });
  }, [isMobile]);
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); focusSearch(); return; }
      if (e.key === "/" && !typing) { e.preventDefault(); focusSearch(); return; }
      if (e.key === "Escape" && !typing) { setSel(null); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [focusSearch]);
  const activateItem = useCallback((item) => {
    if (!item) return;
    if (item.type === "tech") selectTech(railTechById[item.id]);
    else selectClient(clientById[item.id]);
  }, [selectTech, selectClient, railTechById, clientById]);
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, railItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); activateItem(railItems[activeIdx] || railItems[0]); if (isMobile) searchRef.current?.blur(); }
    else if (e.key === "Escape") { if (query) setQuery(""); else { searchRef.current?.blur(); setSel(null); } }
  };
  useEffect(() => { const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`); el && el.scrollIntoView({ block: "nearest" }); }, [activeIdx]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const callClient = (c) => { if (!requireTexting()) return; if (c.phone && window.scoopDial) window.scoopDial(c.phone, c.name); else showToast && showToast("Calling isn't ready — open a tab and try again", "error"); };
  const textClient = (c) => { if (!requireTexting()) return; if (!c.phone) return; setPanelView("messages"); };
  const toggleCard = (k) => { setCardFilter(f => (f === k ? null : k)); if (k === "done" && cardFilter !== "done") setMode("today"); if (k === "onsite" && cardFilter !== "onsite") setMode("today"); };
  const fitAll = () => { const m = mapObj.current, G = window.google; if (!m || !G) return; const b = new G.maps.LatLngBounds(); let n = 0; Object.values(markersRef.current).forEach(e => { b.extend(e.marker.getPosition()); n++; }); Object.values(techOverlaysRef.current).forEach(o => { b.extend({ lat: o.opts.lat, lng: o.opts.lng }); n++; }); if (n) m.fitBounds(b, 40); };
  const distanceTo = (pos, p) => (pos && p ? haversineMiles(pos, { lat: +p.lat, lng: +p.lng }) : null);
  // Who's closest? — every live tech's straight-line distance to this yard, nearest first.
  const closestFor = (c) => {
    const pos = coordsOf(c);
    return technicians.filter(t => t.active !== false).map(t => { const p = presenceByTech[t.id]; const mi = distanceTo(pos, p); return { id: t.id, name: t.name, color: techColor(t.id), presence: p, miles: mi, minutes: mi != null ? estimateMinutes(mi) : null, atClient: onSiteClientOf(p) }; })
      .sort((a, b) => (a.miles ?? 1e9) - (b.miles ?? 1e9) || a.name.localeCompare(b.name));
  };
  const askReassign = async (c, techId) => {
    const t = techById[techId]; if (!c || !t) { setPendingTech(null); return; }
    const ids = await rest(`job_headers?client_id=eq.${c.id}&scheduled_date=gte.${todayStr}&status=eq.scheduled&select=id`);
    setPendingTech({ id: t.id, name: t.name, count: ids.length });
  };
  const confirmReassign = async (c) => {
    if (!pendingTech || !onAssignTech) return;
    setReassigning(true);
    try {
      await onAssignTech(c.id, pendingTech.id, pendingTech.name); // sanctioned client-list flow: client default + future visits cascade
      setLocalTech(o => ({ ...o, [c.id]: { id: pendingTech.id, name: pendingTech.name } }));
      setPendingTech(null);
    } finally { setReassigning(false); }
  };
  useEffect(() => { // props caught up → drop the optimistic override
    setLocalTech(o => { const n = { ...o }; let changed = false; Object.keys(n).forEach(id => { if (clientById[id]?.technician_id === n[id].id) { delete n[id]; changed = true; } }); return changed ? n : o; });
  }, [clientById]);

  // ── Layout numbers ─────────────────────────────────────────────────────────
  const pageH = isMobile ? "auto" : `calc(100vh - ${TOPBAR_H}px - 56px)`;
  const mapH = isMobile ? "calc(100dvh - 300px)" : "auto";
  const liveTechCount = freshPresence.length;
  const subtitle = [`${liveTechCount} tech${liveTechCount === 1 ? "" : "s"} on route`, `${doneCount} of ${totalStops} stops done`, lastPoll ? `updated ${relSecs(now - lastPoll)}` : "syncing…"].join(" · ");
  const unplaced = clients.filter(c => c.active && !coordsOf(c)).length;

  const cards = [
    { k: "done", label: "Stops done today", value: `${doneCount}`, sub: totalStops ? `of ${totalStops} scheduled` : "nothing scheduled", color: "var(--accent)", icon: "✅" },
    { k: "onsite", label: "Techs on-site now", value: `${onSiteCount}`, sub: `${liveTechCount} live on the map`, color: onSiteCount > 0 ? "var(--ok)" : "var(--text)", icon: "📍" },
    { k: "unassigned", label: "Unassigned clients", value: `${unassignedCount}`, sub: unassignedCount > 0 ? "need a tech" : "everyone has a tech", color: unassignedCount > 0 ? AMBER.fg : "var(--text)", icon: "👤" },
    { k: "pastdue", label: "Past due", value: `${pastDueCount}`, sub: pastDueCount > 0 ? "payment pending" : "all paid up", color: pastDueCount > 0 ? "var(--danger)" : "var(--text)", icon: "💳" },
  ];
  const filterGroups = [
    ["Status", [["all", "All"], ["active", "Active"], ["inactive", "Inactive"]]],
    ["Frequency", [["weekly", "Weekly"], ["biweekly", "Bi-weekly"], ["twice-weekly", "Twice a week"]]],
    ["Service day", DAYS.map(d => [d, cap(d)])],
  ];
  const filterLabel = mapFilter === "all" || !mapFilter ? null : (DAYS.includes(mapFilter) ? cap(mapFilter) + "s" : mapFilter === "twice-weekly" ? "Twice a week" : cap(mapFilter));

  // ── Rail rows ──────────────────────────────────────────────────────────────
  const renderTechRow = (t, idx) => {
    const p = t.presence; const on = sel?.type === "tech" && sel.id === t.id;
    const pill = p?.on_site ? { text: "On site", bg: "rgba(26,138,82,.14)", fg: "var(--ok)" } : p ? { text: p.status === "driving" ? "Driving" : "Live", bg: "var(--accent-weak)", fg: "var(--accent)" } : { text: "Offline", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" };
    const sub = p?.on_site ? `at ${t.atClient ? shortName(t.atClient.name) : (p.on_site_client || "a stop")}` : t.stops.length ? `${t.done} of ${t.stops.length} done${t.next ? ` · next ${shortName(t.next.client_name)}` : ""}` : "no stops today";
    return (
      <div key={`t-${t.id}`} data-idx={idx} className={`lm-row${on ? " sel" : ""}${activeIdx === idx && q ? " hov" : ""}`} onClick={() => selectTech(t)}>
        <span style={{ width: 34, height: 34, borderRadius: "50%", background: t.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0, boxShadow: p?.on_site ? `0 0 0 3px ${t.color}44` : "none" }}>{initials(t.name).slice(0, 1)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
          <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: pill.bg, color: pill.fg, flexShrink: 0 }}>{pill.text}</span>
      </div>
    );
  };
  const renderClientRow = (c, idx) => {
    const on = sel?.type === "client" && sel.id === c.id; const h = headerByClient[c.id]; const pill = statusPill(c);
    const tech = techOf(c); const day = getServiceDayForClient(c, serviceArea);
    const sub = mode === "today" && h ? `${h.route_seq ? `#${h.route_seq} · ` : ""}${h.technician_name || "Unassigned"} · ${STATUS_LABEL[h.status] || h.status}` : `${day ? cap(day).slice(0, 3) : "No day"} · ${fmtFreq(c.frequency)}${tech.id ? ` · ${shortName(tech.name || "")}` : " · Unassigned"}`;
    const dot = mode === "today" && h ? STATUS_COLORS[h.status] : isUnassigned(c, localTech) ? UNASSIGNED_COLOR : (DAY_COLORS[day] || "#C7C7CC");
    return (
      <div key={c.id} data-idx={idx} className={`lm-row${on ? " sel" : hovered === c.id ? " hov" : ""}${activeIdx === idx && q ? " hov" : ""}`}
        onMouseEnter={() => setHovered && setHovered(c.id)} onMouseLeave={() => setHovered && setHovered(null)} onClick={() => selectClient(c)}>
        <span style={{ position: "relative", flexShrink: 0 }}>
          <span style={{ width: 34, height: 34, borderRadius: "50%", background: avColor(c.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, opacity: c.active ? 1 : .55 }}>{initials(c.name)}</span>
          <span style={{ position: "absolute", right: -2, bottom: -2, width: 11, height: 11, borderRadius: "50%", background: dot, border: "2px solid var(--surface)" }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
          <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q ? (c.address ? `${c.address}, ${c.zip || ""}` : sub) : sub}</div>
        </div>
        <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: pill.bg, color: pill.fg, flexShrink: 0 }}>{pill.text}</span>
      </div>
    );
  };

  const searchBox = (
    <div style={{ position: "relative" }}>
      <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "var(--text3)", pointerEvents: "none" }}>⌕</span>
      <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={onSearchKey} onFocus={() => isMobile && setSheet("open")}
        placeholder="Search name, address, zip, phone…" aria-label="Search clients" autoFocus={!isMobile}
        style={{ width: "100%", boxSizing: "border-box", padding: "10px 60px 10px 32px", fontSize: 16, borderRadius: 10, border: "1px solid var(--border)", background: "var(--bg)" }} />
      {query ? <button onClick={() => { setQuery(""); searchRef.current?.focus(); }} aria-label="Clear" style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "var(--text3)", fontSize: 16, cursor: "pointer" }}>×</button>
        : !isMobile && <span style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", display: "flex", gap: 3 }}><span className="lm-kbd">⌘K</span></span>}
    </div>
  );
  const railList = (
    <div ref={listRef} className="lm-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 6px 10px" }}>
      {(() => {
        const techBlock = (off) => (<>
          {visibleTechs.length > 0 && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text3)", padding: "8px 10px 4px" }}>Techs · {visibleTechs.length}</div>}
          {visibleTechs.map((t, i) => renderTechRow(t, off + i))}
        </>);
        const clientBlock = (off) => (<>
          {visibleClients.length > 0 && (visibleTechs.length > 0 || q) && <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text3)", padding: "8px 10px 4px" }}>{mode === "today" ? "Today's stops" : "Clients"} · {visibleClients.length}</div>}
          {visibleClients.map((c, i) => renderClientRow(c, off + i))}
        </>);
        return q ? <>{clientBlock(0)}{techBlock(visibleClients.length)}</> : <>{techBlock(0)}{clientBlock(visibleTechs.length)}</>;
      })()}
      {railItems.length === 0 && <div style={{ padding: "28px 12px", textAlign: "center", fontSize: 12.5, color: "var(--text3)" }}>{q ? `Nothing matches “${query}”` : mode === "today" ? "No stops scheduled today" : "No clients match this filter"}</div>}
    </div>
  );
  const railHeader = (
    <div style={{ padding: isMobile ? "10px 12px 6px" : "12px 12px 6px", display: "flex", flexDirection: "column", gap: 8, borderBottom: "1px solid var(--border)" }}>
      {searchBox}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minHeight: 24 }}>
        <span style={{ fontSize: 12, color: "var(--text2)" }}>{q ? `${railItems.length} match${railItems.length === 1 ? "" : "es"}` : `${visibleClients.length} client${visibleClients.length === 1 ? "" : "s"}${visibleTechs.length ? ` · ${visibleTechs.length} tech${visibleTechs.length === 1 ? "" : "s"}` : ""}`}</span>
        <span style={{ flex: 1 }} />
        {cardFilter && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "var(--accent)", background: "var(--accent-weak)", borderRadius: 99, padding: "3px 9px" }}>{cards.find(x => x.k === cardFilter)?.label}<span onClick={() => setCardFilter(null)} style={{ cursor: "pointer", fontWeight: 700 }}>×</span></span>}
        {filterLabel && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "var(--accent)", background: "var(--accent-weak)", borderRadius: 99, padding: "3px 9px" }}>{filterLabel}<span onClick={() => setMapFilter && setMapFilter("all")} style={{ cursor: "pointer", fontWeight: 700 }}>×</span></span>}
        <div style={{ position: "relative" }}>
          <button type="button" onClick={() => setFilterOpen(o => !o)} style={{ background: filterLabel ? "var(--accent-weak)" : "transparent", border: "1px solid var(--border)", borderRadius: 8, padding: "4px 9px", fontSize: 12, fontWeight: 500, color: filterLabel ? "var(--accent)" : "var(--text2)", cursor: "pointer", font: "inherit" }}>Filter ▾</button>
          {filterOpen && (<>
            <div onClick={() => setFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
            <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 41, width: 200, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-lg)", padding: 8, maxHeight: 360, overflowY: "auto" }}>
              {filterGroups.map(([gname, opts]) => (
                <div key={gname} style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text3)", padding: "6px 8px 3px" }}>{gname}</div>
                  {opts.map(([val, lab]) => (
                    <button key={val} type="button" onClick={() => { setMapFilter && setMapFilter(val); setFilterOpen(false); }}
                      style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: (mapFilter || "all") === val ? "var(--accent-weak)" : "transparent", color: (mapFilter || "all") === val ? "var(--accent)" : "var(--text)", borderRadius: 7, padding: "6px 9px", fontSize: 13, cursor: "pointer", font: "inherit" }}>{lab}</button>
                  ))}
                </div>
              ))}
            </div>
          </>)}
        </div>
      </div>
    </div>
  );

  // ── Detail panel: client ───────────────────────────────────────────────────
  const renderClientPanel = (c) => {
    const pill = statusPill(c); const tech = techOf(c); const h = headerByClient[c.id];
    const day = getServiceDayForClient(c, serviceArea);
    const closest = closestFor(c);
    const avg = cleanMap[c.id]?.avgMin;
    const monthly = clientMonthly(c, pricing);
    const lastServiced = c.last_service_date || recent.find(r => r.status === "completed")?.scheduled_date || null;
    const fields = [
      ["Service day", day ? cap(day) + "s" + (hasDayOverride(c) ? " (custom)" : "") : "No service day"],
      ["Next visit", nextVisit ? `${fmtDate(nextVisit.scheduled_date)}${nextVisit.scheduled_date === todayStr ? " · today" : ""}` : "Not scheduled"],
      ["Avg clean time", avg != null ? fmtClean(avg) : "No timed visits yet"],
      ["Dogs · deo", `${c.dogs || 0} dog${Number(c.dogs) === 1 ? "" : "s"}${c.deo_enabled ? " · 💨 deodorizer" : ""}`],
      ["Plan", `$${monthly.toFixed(0)}/mo · ${fmtFreq(c.frequency)}`],
      ["Last serviced", lastServiced ? fmtDate(lastServiced) : "Never"],
      ["Address", [c.address, c.zip].filter(Boolean).join(", ") || "—"],
    ];
    const techOptions = closest.map(t => ({ value: t.id, label: `${t.name}${t.miles != null ? ` · ${t.miles.toFixed(1)} mi` : ""}` }));
    return (
      <>
        <div style={{ display: "flex", gap: 12, alignItems: "center", paddingRight: 30 }}>
          <span style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: avColor(c.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 600 }}>{initials(c.name)}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3, minWidth: 0 }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: pill.bg, color: pill.fg, flexShrink: 0 }}>{pill.text}</span>
              {h && <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: `${STATUS_COLORS[h.status]}22`, color: STATUS_COLORS[h.status], flexShrink: 0 }}>{STATUS_LABEL[h.status] || h.status} today</span>}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text2)", marginTop: 10 }}>📍 {c.address || "No address"}{c.zip ? `, ${c.zip}` : ""}</div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={() => callClient(c)} disabled={!c.phone} style={{ flex: 1.6, minWidth: 0, background: "var(--accent)", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: c.phone ? 1 : .5 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📞 {c.phone ? fmtPhone(c.phone) : "Call"}</span>
          </button>
          <button className="btn btn-ghost" onClick={() => textClient(c)} disabled={!c.phone} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: c.phone ? 1 : .5 }}>💬 Text</button>
        </div>

        {/* Assigned tech + inline Reassign (nearest-first) */}
        <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 10, background: "var(--bg)", border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <span style={{ fontSize: 12, color: "var(--text2)" }}>Assigned tech</span>
            {tech.id ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: techColor(tech.id) }} />{tech.name || "Assigned"}</span>
              : <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(215,50,43,.10)", color: "var(--danger)" }}>Unassigned</span>}
          </div>
          <select value={pendingTech?.id || ""} onChange={e => e.target.value ? askReassign(c, e.target.value) : setPendingTech(null)} disabled={reassigning || !onAssignTech}
            style={{ width: "100%", marginTop: 8, fontSize: 16, padding: "8px 10px", borderRadius: 8 }}>
            <option value="">{tech.id ? "Reassign to… (nearest first)" : "Assign a tech… (nearest first)"}</option>
            {techOptions.filter(o => o.value !== tech.id).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {pendingTech && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 10px", borderRadius: 9, background: "var(--accent-weak)", border: "1px solid rgba(0,113,227,.2)" }}>
              <span style={{ fontSize: 12.5, color: "var(--accent)", fontWeight: 600, flex: 1, minWidth: 140 }}>Move {pendingTech.count} future visit{pendingTech.count === 1 ? "" : "s"} to {pendingTech.name.split(" ")[0]}?</span>
              <button className="btn btn-gold btn-sm" disabled={reassigning} onClick={() => confirmReassign(c)}>{reassigning ? "Moving…" : "Move"}</button>
              <button className="btn btn-ghost btn-sm" disabled={reassigning} onClick={() => setPendingTech(null)}>Cancel</button>
            </div>
          )}
        </div>

        {/* Who's closest? */}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 7 }}>Who's closest right now?</div>
          {closest.filter(t => t.presence).length === 0
            ? <div style={{ fontSize: 12, color: "var(--text3)" }}>No techs are live on the map right now.</div>
            : closest.filter(t => t.presence).slice(0, 4).map(t => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: t.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials(t.name).slice(0, 1)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>{t.miles != null ? `${t.miles.toFixed(1)} mi · ~${t.minutes} min` : "no position"}{t.atClient ? ` · at ${shortName(t.atClient.name)}` : t.presence?.on_site ? " · on site" : ""}</div>
                </div>
                {t.id !== tech.id && onAssignTech && <button className="btn btn-ghost btn-xs" onClick={() => askReassign(c, t.id)} disabled={reassigning}>{tech.id ? "Move" : "Assign"}</button>}
                {t.id === tech.id && <span style={{ fontSize: 10.5, color: "var(--text3)", fontWeight: 600 }}>assigned</span>}
              </div>
            ))}
        </div>

        <div style={{ marginTop: 12 }}>{fields.map(([l, v]) => <div key={l} className="lm-field"><span>{l}</span><span>{v}</span></div>)}</div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 8 }}>Recent visits</div>
          {recent.length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>No visits on record yet.</div> : recent.map(r => (
            <div key={r.id} style={{ display: "flex", gap: 9, padding: "4px 0", alignItems: "flex-start" }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: STATUS_COLORS[r.status] || "#C7C7CC", marginTop: 5, flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, color: "var(--text)" }}>{STATUS_LABEL[r.status] || cap(r.status)}{r.technician_name ? ` · ${shortName(r.technician_name)}` : ""}{r.duration_mins ? ` · ${r.duration_mins} min` : ""}</div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>{fmtDate(r.scheduled_date)}</div>
              </div>
            </div>
          ))}
        </div>
        {onViewProfile && <button className="btn btn-ghost btn-sm" onClick={() => onViewProfile(c)} style={{ width: "100%", marginTop: 14 }}>View full profile →</button>}
      </>
    );
  };

  // ── Detail panel: tech's day ───────────────────────────────────────────────
  const renderTechPanel = (t) => {
    const p = t.presence; const pos = p ? { lat: +p.lat, lng: +p.lng } : null;
    const nextClient = t.next ? clientById[t.next.client_id] : null;
    const driveMin = pos && nextClient ? estimateMinutes(haversineMiles(pos, coordsOf(nextClient))) : (t.next?.route_leg_minutes ?? null);
    let dwell = 0;
    if (p?.on_site && t.atClient) { const h = headerByClient[t.atClient.id]; const elapsed = h?.started_at ? (now - new Date(h.started_at).getTime()) / 60000 : 0; dwell = Math.max(0, (cleanMap[t.atClient.id]?.avgMin ?? 8) - elapsed); }
    const eta = driveMin != null ? Math.round(driveMin + dwell) : null;
    const pill = p?.on_site ? { text: "On site", bg: "rgba(26,138,82,.14)", fg: "var(--ok)" } : p ? { text: p.status === "driving" ? "Driving" : "Live", bg: "var(--accent-weak)", fg: "var(--accent)" } : { text: "Offline", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" };
    const stat = (label, value, color) => (
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: "8px 10px", minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: "var(--text2)" }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-.02em", color: color || "var(--text)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      </div>
    );
    return (
      <>
        <div style={{ display: "flex", gap: 12, alignItems: "center", paddingRight: 30 }}>
          <span style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: t.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, boxShadow: p?.on_site ? `0 0 0 4px ${t.color}33` : "none" }}>{initials(t.name).slice(0, 1)}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3 }}>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: pill.bg, color: pill.fg }}>{pill.text}</span>
              <span style={{ fontSize: 11.5, color: "var(--text2)" }}>{p ? `updated ${relSecs(now - new Date(p.updated_at).getTime())}` : "no live position"}</span>
            </div>
          </div>
        </div>
        {p?.on_site && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--text)", background: "rgba(26,138,82,.10)", border: "1px solid rgba(26,138,82,.25)", borderRadius: 9, padding: "7px 10px" }}>📍 In the yard at <strong>{t.atClient ? t.atClient.name : p.on_site_client}</strong></div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
          {stat("Done", `${t.done} / ${t.stops.length}`, "var(--ok)")}
          {stat("Remaining", `${t.remaining}`)}
          {stat("Next stop", nextClient ? shortName(nextClient.name) : t.remaining ? "—" : "Route done 🎉")}
          {stat("ETA", eta != null && nextClient ? `~${eta} min` : "—", "var(--accent)")}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button className={`btn btn-sm ${showTrail ? "btn-primary" : "btn-ghost"}`} onClick={() => setShowTrail(v => !v)} title="Where they've actually driven today">🧭 {showTrail ? "Hide trail" : "Show trail"}</button>
          {setPage && <button className="btn btn-ghost btn-sm" onClick={() => setPage("route")}>📋 Today's Route →</button>}
        </div>
        {showTrail && <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>{trail.length ? `${trail.length} pings today` : "Loading today's pings…"}</div>}
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 6 }}>Today's stops</div>
          {t.stops.length === 0 ? <div style={{ fontSize: 12, color: "var(--text3)" }}>No stops scheduled today.</div> : t.stops.map(s => {
            const c = clientById[s.client_id]; const isNext = t.next && t.next.id === s.id;
            return (
              <div key={s.id} onClick={() => c && selectClient(c)} style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 6px", borderRadius: 8, cursor: c ? "pointer" : "default", background: isNext ? "var(--accent-weak)" : "transparent" }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: STATUS_COLORS[s.status] || "#9CA3AF", color: "#fff", fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.route_seq ?? "·"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: s.status === "skipped" ? "line-through" : "none" }}>{s.client_name}</div>
                  <div style={{ fontSize: 11, color: "var(--text2)" }}>{STATUS_LABEL[s.status] || s.status}{s.route_leg_minutes ? ` · ${s.route_leg_minutes} min drive` : ""}{cleanMap[s.client_id]?.avgMin ? ` · ${fmtClean(cleanMap[s.client_id].avgMin)} clean` : ""}</div>
                </div>
                {isNext && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--accent)" }}>NEXT</span>}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const panelBody = sel && (
    panelView === "messages" && selClient
      ? <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingRight: 30 }}>
            <button className="lm-ib" onClick={() => setPanelView("detail")} aria-label="Back">‹</button>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Text {selClient.name}</span>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}><Conversations scope="client" rows={clients} showToast={showToast} openId={selClient.id} /></div>
        </div>
      : selClient ? renderClientPanel(selClient) : selTech ? renderTechPanel(selTech) : null
  );
  const panelW = panelView === "messages" ? "min(680px, 100%)" : PANEL_W;

  // ── Legend ─────────────────────────────────────────────────────────────────
  const legend = (
    <div style={{ position: "absolute", left: 12, ...(isMobile ? { top: 12 } : { bottom: 12 }), zIndex: 5, background: "rgba(255,255,255,.9)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow)", fontSize: 11, color: "var(--text2)", maxWidth: 210 }}>
      <button onClick={() => setLegendOpen(o => !o)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", border: "none", background: "transparent", padding: "8px 11px", font: "inherit", fontSize: 11, fontWeight: 700, color: "var(--text)", cursor: "pointer" }}>Legend <span style={{ color: "var(--text3)" }}>{legendOpen ? "▾" : "▸"}</span></button>
      {legendOpen && (
        <div style={{ padding: "0 11px 10px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}><span style={{ width: 16, height: 16, borderRadius: "50%", background: TECH_PALETTE[0], color: "#fff", fontSize: 9, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,.1)" }}>K</span>Tech (ring = on site)</div>
          {mode === "today" ? (<>
            <div style={{ fontWeight: 600, fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".05em", margin: "4px 0" }}>Stop status</div>
            {[["scheduled", "Scheduled"], ["started", "Started"], ["completed", "Done"], ["skipped", "Skipped"]].map(([k, l]) => <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: STATUS_COLORS[k] }} />{l}</div>)}
            <div style={{ marginTop: 4, color: "var(--text3)" }}>Line = tech's route · number = stop order</div>
          </>) : (<>
            <div style={{ fontWeight: 600, fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".05em", margin: "4px 0" }}>Color = service day</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 8px" }}>
              {Object.entries(DAY_COLORS).map(([d, col]) => <div key={d} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: col }} />{cap(d).slice(0, 3)}</div>)}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: UNASSIGNED_COLOR }} />No tech</div>
            </div>
            <div style={{ fontWeight: 600, fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".05em", margin: "6px 0 3px" }}>Shape = frequency</div>
            <div style={{ display: "flex", gap: 10 }}><span>● Weekly</span><span>◆ Bi-weekly</span><span>★ 2×/wk</span></div>
            <div style={{ marginTop: 5, display: "flex", flexDirection: "column", gap: 2 }}>
              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", border: "2px solid #D7322B", verticalAlign: -1, marginRight: 5 }} />Red ring = past due</span>
              <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", border: "2px solid #E9A93C", verticalAlign: -1, marginRight: 5 }} />Amber ring = paused</span>
              <span>💨 = deodorizer</span>
            </div>
            {mode === "territory" && <div style={{ marginTop: 5, color: "var(--text3)" }}>ZIP tint = day · darker = more clients · click a ZIP to list it</div>}
          </>)}
        </div>
      )}
    </div>
  );

  // ── Map toolbar (top-right chips) ──────────────────────────────────────────
  const toolbar = (
    <div style={{ position: "absolute", top: 12, right: sel && !isMobile ? `calc(${panelW}${typeof panelW === "number" ? "px" : ""} + 12px)` : 12, zIndex: 5, display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: "60%", transition: "right .26s cubic-bezier(.2,.8,.2,1)" }}>
      {mode === "territory" && <button className={`lm-chip${showHeatmap ? " on" : ""}`} onClick={() => setShowHeatmap(v => !v)}>🔥 Density</button>}
      {mode === "territory" && <button className={`lm-chip${showRevenue ? " on" : ""}`} onClick={() => setShowRevenue(v => !v)}>💰 Revenue</button>}
      {mode === "territory" && zctaLoading && <span className="lm-chip" style={{ cursor: "default" }}>⏳ ZIP shapes…</span>}
      <button className="lm-chip" onClick={fitAll} title="Fit everything on screen">⤢ Fit all</button>
      {!isMobile && !railOpen && <button className="lm-chip" onClick={focusSearch}>⌕ Search <span className="lm-kbd" style={{ marginLeft: 2 }}>⌘K</span></button>}
    </div>
  );

  const zipTip = zipHover && zipStats && (() => {
    const s = zipStats[zipHover.zip] || { count: 0, active: 0, revenue: 0 };
    let day = null; Object.entries(serviceArea || {}).forEach(([d, zips]) => { if ((zips || []).includes(zipHover.zip)) day = d; });
    return (
      <div style={{ position: "absolute", left: zipHover.x + 14, top: zipHover.y + 14, zIndex: 6, pointerEvents: "none", background: "rgba(29,29,31,.92)", color: "#fff", padding: "7px 10px", borderRadius: 9, fontSize: 12, whiteSpace: "nowrap", boxShadow: "var(--shadow)" }}>
        <div style={{ fontWeight: 700 }}>{zipHover.zip} · <span style={{ color: day ? DAY_COLORS[day] : "#C7C7CC" }}>{day ? cap(day) : "Not serviced"}</span></div>
        <div style={{ opacity: .85, marginTop: 2 }}>{s.active} active client{s.active === 1 ? "" : "s"} · ${Math.round(s.revenue).toLocaleString()}/mo</div>
      </div>
    );
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: pageH, minHeight: isMobile ? undefined : 560, overflowX: "clip", position: "relative", paddingBottom: isMobile ? 112 : 0 /* room to scroll the map above the fixed bottom sheet */ }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "var(--text)", letterSpacing: "-.02em", display: "flex", alignItems: "center", gap: 8 }}>
            Live Map
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: liveTechCount ? "var(--ok)" : "var(--text3)", boxShadow: liveTechCount ? "0 0 0 3px rgba(26,138,82,.18)" : "none" }} title={liveTechCount ? "Live" : "No techs live"} />
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text2)", marginTop: 2 }}>{subtitle}{unplaced > 0 ? ` · ${unplaced} not on map` : ""}</div>
        </div>
        <div className="seg" role="tablist" aria-label="Map mode">
          {[["today", "Today"], ["all", "All Clients"], ["territory", "Territory"]].map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={mode === v} className={mode === v ? "on" : ""} onClick={() => { setMode(v); if (v !== "today" && (cardFilter === "done" || cardFilter === "onsite")) setCardFilter(null); }} style={isMobile ? { padding: "7px 12px", fontSize: 12.5 } : undefined}>{l}</button>
          ))}
        </div>
      </div>

      {/* Metric row — every card is a filter */}
      <div style={isMobile ? { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2, marginLeft: -13, marginRight: -13, paddingLeft: 13, paddingRight: 13 } : { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
        {cards.map(cd => (
          <button key={cd.k} type="button" className={`lm-card${cardFilter === cd.k ? " on" : ""}`} onClick={() => toggleCard(cd.k)} style={isMobile ? { flex: "0 0 150px", padding: "10px 12px" } : undefined}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--text2)" }}><span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cd.label}</span><span style={{ fontSize: 12 }}>{cd.icon}</span></div>
            <div style={{ fontSize: isMobile ? 22 : 25, fontWeight: 700, letterSpacing: "-.02em", color: cd.color, marginTop: 4, lineHeight: 1.1 }}>{cd.value}</div>
            <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cd.sub}</div>
          </button>
        ))}
      </div>

      {/* Rail + Map + Panel */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: 12, position: "relative" }}>
        {!isMobile && (
          <div style={{ width: railOpen ? RAIL_W : 0, flexShrink: 0, transition: "width .22s cubic-bezier(.2,.8,.2,1)", overflow: "hidden", display: "flex", flexDirection: "column", background: "var(--surface)", border: railOpen ? "1px solid var(--border)" : "none", borderRadius: "var(--r-lg)", boxShadow: railOpen ? "var(--shadow)" : "none", minHeight: 0 }}>
            <div style={{ width: RAIL_W, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
              {railHeader}
              {railList}
            </div>
          </div>
        )}

        <div ref={wrapRef} style={{ flex: 1, minWidth: 0, position: "relative", borderRadius: "var(--r-lg)", overflow: "hidden", border: "1px solid var(--border)", boxShadow: "var(--shadow)", background: "#e8e8ed", height: mapH, minHeight: isMobile ? 380 : undefined }}>
          {!mapReady && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "var(--text2)" }}>
              <div style={{ fontSize: 28 }}>🗺️</div><div style={{ fontWeight: 600 }}>Loading map…</div>
            </div>
          )}
          <div ref={mapRef} style={{ position: "absolute", inset: 0 }} />
          {!isMobile && (
            <button onClick={() => setRailOpen(o => !o)} aria-label={railOpen ? "Collapse list" : "Expand list"} title={railOpen ? "Collapse list" : "Expand list"}
              style={{ position: "absolute", left: 12, top: 12, zIndex: 5, width: 30, height: 30, borderRadius: 8, border: "1px solid var(--border)", background: "rgba(255,255,255,.92)", backdropFilter: "blur(10px)", cursor: "pointer", fontSize: 14, color: "var(--text2)", boxShadow: "var(--shadow-sm)" }}>{railOpen ? "‹" : "›"}</button>
          )}
          {mapReady && toolbar}
          {mapReady && legend}
          {zipTip}

          {/* Right detail panel — slides over the map, never navigates away */}
          {!isMobile && (
            <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: panelW, maxWidth: "calc(100% - 24px)", zIndex: 7, transform: sel ? "translateX(0)" : "translateX(105%)", transition: "transform .26s cubic-bezier(.2,.8,.2,1), width .2s", background: "var(--surface)", borderLeft: "1px solid var(--border)", boxShadow: "-8px 0 32px rgba(0,0,0,.10)", display: "flex", flexDirection: "column", minHeight: 0 }}>
              {sel && <button onClick={closePanel} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, borderRadius: "50%", border: "none", background: "var(--bg)", color: "var(--text2)", fontSize: 18, lineHeight: 1, cursor: "pointer", zIndex: 2 }}>×</button>}
              <div className="lm-scroll" style={{ flex: 1, minHeight: 0, overflowY: panelView === "messages" ? "hidden" : "auto", padding: 16 }}>{panelBody}</div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: rail = bottom sheet; detail = sheet over it */}
      {isMobile && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 100, background: "var(--surface)", borderRadius: "18px 18px 0 0", boxShadow: "0 -8px 32px rgba(0,0,0,.16)", border: "1px solid var(--border)", borderBottom: "none", height: sheet === "open" ? "62vh" : "auto", maxHeight: "62vh", display: "flex", flexDirection: "column", transition: "height .22s" }}>
          <div onClick={() => setSheet(s => (s === "open" ? "peek" : "open"))} style={{ display: "flex", justifyContent: "center", padding: "7px 0 0", cursor: "pointer" }}><span style={{ width: 36, height: 4, borderRadius: 2, background: "rgba(0,0,0,.18)" }} /></div>
          {railHeader}
          {sheet === "open" && railList}
        </div>
      )}
      {isMobile && sel && (<>
        <div onClick={closePanel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 101 }} />
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 102, background: "var(--surface)", borderRadius: "20px 20px 0 0", maxHeight: "85dvh", display: "flex", flexDirection: "column", boxShadow: "0 -8px 40px rgba(0,0,0,.28)" }}>
          <button onClick={closePanel} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, borderRadius: "50%", border: "none", background: "var(--bg)", color: "var(--text2)", fontSize: 18, lineHeight: 1, cursor: "pointer", zIndex: 2 }}>×</button>
          <div className="lm-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16, height: panelView === "messages" ? "80vh" : undefined }}>{panelBody}</div>
        </div>
      </>)}
    </div>
  );
}
